import { useEffect, useState } from "react";
import { apiFetch } from "../utility/apiFetch.js";
import * as Sentry from "@sentry/react";
import { Link } from "react-router-dom";
import Layout from "../layouts/Layout.jsx";
import Spinner from "../components/Spinner.jsx";
import InlineEdit from "../components/InlineEdit.jsx";
import NoDevices from "../components/NoDevices.jsx";
import GrowCard from "../components/GrowCard.jsx";
import { useGrowsQuery } from "../hooks/useGrowsQuery.js";
import { useRecipesQuery } from "../hooks/useRecipesQuery.js";
import { useInventoryQuery, useLowStockQuery } from "../hooks/useInventoryQuery.js";
import { formatDistanceToNow } from "date-fns";
import { computeVPD } from "../utility/vpd.js";
import usePlan from "../hooks/usePlan.js";

const API = import.meta.env.VITE_API_BASE_URL;

const METRIC_UNITS = { temperature: "°F", humidity: "%", co2: "ppm" };
const METRIC_LABELS = { temperature: "Temp", humidity: "RH", co2: "CO2" };

const TYPE_LABELS = {
  liquid_culture: "Liquid Culture",
  bulk_spawn: "Bulk Spawn",
  substrate: "Substrate",
  other: "Other",
};
const TYPE_COLORS = {
  liquid_culture: "bg-blue-100 text-blue-700",
  bulk_spawn: "bg-amber-100 text-amber-700",
  substrate: "bg-emerald-100 text-emerald-700",
  other: "bg-gray-100 text-gray-600",
};

function metricColor(value, limitLow, limitHigh) {
  if (value == null) return "text-gray-400";
  if (value < limitLow) return "text-blue-700";
  if (value > limitHigh) return "text-red-600";
  return "text-green-600";
}

function vpdColor(vpd) {
  if (vpd == null) return "text-gray-400";
  if (vpd < 0.1) return "text-blue-700";
  if (vpd < 0.5) return "text-green-600";
  if (vpd < 0.9) return "text-yellow-500";
  return "text-red-600";
}

function SectionHeader({ title, href, linkLabel }) {
  return (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">{title}</h2>
      {href && (
        <Link to={href} className="text-xs text-cyan-600 hover:text-cyan-800 font-medium">
          {linkLabel ?? "View all"} →
        </Link>
      )}
    </div>
  );
}

export default function Dashboard() {
  const [dashLoading, setDashLoading] = useState(true);
  const [dashError, setDashError] = useState(null);
  const [dashboard, setDashboard] = useState(null);

  const { data: grows } = useGrowsQuery();
  const { data: recipes } = useRecipesQuery();
  const { data: inventoryItems } = useInventoryQuery();
  const { data: lowStockItems } = useLowStockQuery();
  const { isPro, planLoading } = usePlan();

  const getDashboard = async () => {
    try {
      const res = await fetch(API + "dashboard", { credentials: "include" });
      if (res.status !== 200) { setDashError(await res.text()); return; }
      const data = await res.json();
      setDashboard(data || null);
      setDashLoading(false);
    } catch (e) {
      Sentry.captureException(e);
      setDashError(e.message);
    }
  };

  useEffect(() => {
    getDashboard();
    const es = new EventSource(API + "dashboardlive", { withCredentials: true });
    es.addEventListener("metrics", (e) => {
      const updates = JSON.parse(e.data);
      setDashboard(prev => {
        if (!prev) return prev;
        const next = structuredClone(prev);
        updates.forEach(u => {
          const d = next.devices[u.device_id];
          if (!d) return;
          d.device.last_seen = u.last_seen;
          u.sensors.forEach(su => {
            const s = d.sensors.find(s => s.id === su.id);
            if (!s) return;
            su.metrics.forEach(mu => {
              const m = s.metrics.find(m => m.metric === mu.metric);
              if (m) m.latest_value = mu.latest_value;
            });
          });
        });
        return next;
      });
    });
    return () => es.close();
  }, []);

  const saveName = async (type, id, name) => {
    const path = type === "device" ? `devices/${id}/name` : `sensors/${id}/name`;
    await apiFetch(path, { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ name }) });
    setDashboard(prev => {
      const next = structuredClone(prev);
      if (type === "device") {
        Object.values(next.devices).forEach(d => { if (d.device.id === id) d.device.display_name = name || null; });
      } else {
        Object.values(next.devices).forEach(d => d.sensors.forEach(s => { if (s.id === id) s.display_name = name || null; }));
      }
      return next;
    });
  };

  if (dashLoading) return <Layout title="Dashboard"><Spinner /></Layout>;
  if (dashError) return <Layout title="Dashboard"><div className="p-4 text-red-600">{dashError}</div></Layout>;

  const thresholds = dashboard ? (Object.values(dashboard.devices ?? {})[0]?.thresholds?.thresholds || {}) : {};
  const activeGrows = (grows ?? []).filter(g => g.status === "active");
  const latestRecipe = (recipes ?? []).sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];

  function liveDeviceFor(grow) {
    if (!grow.device_id || !dashboard?.devices) return null;
    return dashboard.devices[grow.device_id] ?? null;
  }

  return (
    <Layout title="Dashboard">
      <div className="container w-full space-y-8">

        {/* Sensors */}
        <section>
          <SectionHeader title="Sensors" href="/dashboard/devices" linkLabel="All devices" />
          {!dashboard || Object.keys(dashboard.devices ?? {}).length === 0 ? (
            <NoDevices refreshDashboards={getDashboard} />
          ) : (
            <div className="space-y-3">
              {Object.values(dashboard.devices).map(({ device, sensors }) => (
                <div key={device.id} className="rounded-xl bg-white shadow p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h3 className="text-cyan-800 font-semibold flex items-center gap-2">
                        <InlineEdit
                          value={device.display_name}
                          placeholder={device.hostname}
                          onSave={name => saveName("device", device.id, name)}
                        />
                        <span className="text-xs font-mono text-gray-400 font-normal">{device.connectcode}</span>
                      </h3>
                      <div className="text-xs text-gray-400 mt-0.5">
                        Updated {device.last_seen ? formatDistanceToNow(new Date(device.last_seen), { addSuffix: true }) : "never"}
                      </div>
                    </div>
                    <Link
                      to={`/dashboard/${device.id}`}
                      className="text-sm text-cyan-600 hover:text-cyan-800 font-medium border border-cyan-200 rounded px-3 py-1 hover:bg-cyan-50 transition-colors"
                    >
                      Details →
                    </Link>
                  </div>
                  <div className="space-y-2">
                    {sensors.map(sensor => {
                      const isOrange = sensor.sensor_code === "orange1";
                      const tempMetric = sensor.metrics.find(m => m.metric === "temperature");
                      const humMetric = sensor.metrics.find(m => m.metric === "humidity");
                      const vpd = (tempMetric?.latest_value != null && humMetric?.latest_value != null)
                        ? computeVPD(tempMetric.latest_value, humMetric.latest_value) : null;
                      return (
                        <div key={sensor.id} className="flex items-center gap-4 py-1.5 border-t border-gray-50">
                          <div className="flex items-center gap-2 w-40 shrink-0">
                            <span className={`w-2 h-2 rounded-full shrink-0 ${isOrange ? "bg-orange-500" : "bg-cyan-500"}`} />
                            <InlineEdit
                              value={sensor.display_name}
                              placeholder={sensor.sensor_name}
                              onSave={name => saveName("sensor", sensor.id, name)}
                              className="text-sm font-medium text-gray-700"
                            />
                          </div>
                          <div className="flex gap-5 flex-wrap">
                            {sensor.metrics.map(metric => {
                              const t = thresholds[metric.metric];
                              const color = t ? metricColor(metric.latest_value, t.low_value, t.high_value) : "text-gray-600";
                              return (
                                <div key={metric.id} className="text-center min-w-[48px]">
                                  <div className={`text-base font-bold tabular-nums ${color}`}>
                                    {metric.latest_value != null ? Math.round(metric.latest_value * 10) / 10 : "--"}
                                    <span className="text-xs font-normal ml-0.5">{METRIC_UNITS[metric.metric]}</span>
                                  </div>
                                  <div className="text-xs text-gray-400">{METRIC_LABELS[metric.metric]}</div>
                                </div>
                              );
                            })}
                            {vpd != null && (
                              <div className="text-center min-w-[48px]">
                                <div className={`text-base font-bold tabular-nums ${vpdColor(vpd)}`}>
                                  {vpd.toFixed(2)}<span className="text-xs font-normal ml-0.5">kPa</span>
                                </div>
                                <div className="text-xs text-gray-400">VPD</div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* Active Grows */}
        <section>
          <SectionHeader title="Active Grows" href="/grows" />
          {planLoading ? (
            <div className="rounded-xl bg-white shadow p-6 text-center text-gray-300 text-sm animate-pulse">Loading…</div>
          ) : !isPro ? (
            <div className="rounded-xl bg-white shadow p-6 flex flex-col items-center gap-2 text-center">
              <div className="text-2xl">🌱</div>
              <div className="text-sm font-medium text-gray-700">Track your grows with Pro</div>
              <div className="text-xs text-gray-400 max-w-xs">
                Upgrade to Pro to log grows, track phases, and record harvests.
                To upgrade, email{" "}
                <a href="mailto:hello@fungistics.com" className="text-cyan-600 underline">hello@fungistics.com</a>.
              </div>
              <Link to="/grows" className="mt-1 text-xs text-cyan-600 hover:text-cyan-800 font-medium">
                View grows →
              </Link>
            </div>
          ) : activeGrows.length === 0 ? (
            <div className="rounded-xl bg-white shadow p-6 text-center text-gray-400 text-sm">
              No active grows · <Link to="/grows/new" className="text-cyan-600 hover:underline">Start one</Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {activeGrows.slice(0, 2).map(grow => (
                <GrowCard key={grow.id} grow={grow} liveDevice={liveDeviceFor(grow)} hostname={liveDeviceFor(grow)?.device?.hostname} />
              ))}
              {activeGrows.length > 2 && (
                <Link to="/grows" className="flex items-center justify-center rounded-xl border-2 border-dashed border-cyan-200 text-cyan-600 hover:bg-cyan-50 text-sm font-medium p-6 transition-colors">
                  +{activeGrows.length - 2} more grows →
                </Link>
              )}
            </div>
          )}
        </section>

        {/* Low stock alert */}
        {lowStockItems && lowStockItems.length > 0 && (
          <section>
            <SectionHeader title="Low Stock" href="/inventory" linkLabel="View inventory" />
            <div className="rounded-xl bg-white shadow divide-y divide-gray-50">
              {lowStockItems.map((item) => (
                <Link key={item.id} to={`/inventory/${item.id}`} className="flex items-center justify-between px-4 py-3 hover:bg-amber-50 transition-colors">
                  <div>
                    <span className="text-sm font-medium text-gray-800">{item.name}</span>
                    {item.species && <span className="text-xs text-gray-400 ml-2">{item.species}</span>}
                  </div>
                  <span className="text-sm font-semibold text-amber-600 tabular-nums">
                    {item.quantity} {item.unit} left
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Latest Recipe + Analytics + Inventory teasers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <section>
            <SectionHeader title="Latest Recipe" href="/recipes" />
            {planLoading ? (
              <div className="rounded-xl bg-white shadow p-6 text-center text-gray-300 text-sm animate-pulse">Loading…</div>
            ) : !isPro ? (
              <div className="rounded-xl bg-white shadow p-6 flex flex-col items-center gap-2 text-center">
                <div className="text-2xl">📋</div>
                <div className="text-sm font-medium text-gray-700">Save recipes with Pro</div>
                <div className="text-xs text-gray-400 max-w-xs">
                  Upgrade to Pro to build a recipe library with ingredients and procedures.
                  Email{" "}
                  <a href="mailto:hello@fungistics.com" className="text-cyan-600 underline">hello@fungistics.com</a> to upgrade.
                </div>
                <Link to="/recipes" className="mt-1 text-xs text-cyan-600 hover:text-cyan-800 font-medium">
                  View recipes →
                </Link>
              </div>
            ) : !latestRecipe ? (
              <div className="rounded-xl bg-white shadow p-6 text-center text-gray-400 text-sm">
                No recipes yet · <Link to="/recipes/new" className="text-cyan-600 hover:underline">Create one</Link>
              </div>
            ) : (
              <Link to={`/recipes/${latestRecipe.id}`} className="block rounded-xl bg-white shadow p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-gray-800">{latestRecipe.name}</div>
                    {latestRecipe.species && (
                      <div className="text-xs text-gray-500 mt-0.5">{latestRecipe.species}</div>
                    )}
                  </div>
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full shrink-0 ${TYPE_COLORS[latestRecipe.recipe_type] ?? "bg-gray-100 text-gray-600"}`}>
                    {TYPE_LABELS[latestRecipe.recipe_type] ?? latestRecipe.recipe_type}
                  </span>
                </div>
                {latestRecipe.description && (
                  <p className="text-sm text-gray-500 mt-2 line-clamp-2">{latestRecipe.description}</p>
                )}
              </Link>
            )}
          </section>

          <section>
            <SectionHeader title="Analytics" href="/analytics" linkLabel="Open" />
            <Link to="/analytics" className="flex flex-col justify-between rounded-xl bg-white shadow p-4 hover:shadow-md transition-shadow min-h-[80px]">
              <div className="text-sm text-gray-500">Historical sensor trends and insights</div>
              <div className="mt-3 text-xs text-cyan-600 font-medium">View analytics →</div>
            </Link>
          </section>

          <section>
            <SectionHeader title="Inventory" href="/inventory" />
            <Link to="/inventory" className="flex flex-col justify-between rounded-xl bg-white shadow p-4 hover:shadow-md transition-shadow min-h-[80px]">
              <div className="flex items-center gap-3">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-800 tabular-nums">{inventoryItems?.length ?? "—"}</div>
                  <div className="text-xs text-gray-400">items</div>
                </div>
                {lowStockItems && lowStockItems.length > 0 && (
                  <div className="text-center">
                    <div className="text-2xl font-bold text-amber-500 tabular-nums">{lowStockItems.length}</div>
                    <div className="text-xs text-gray-400">low stock</div>
                  </div>
                )}
              </div>
              <div className="mt-3 text-xs text-cyan-600 font-medium">Manage inventory →</div>
            </Link>
          </section>
        </div>

      </div>
    </Layout>
  );
}
